import type React from 'react';
import { KebabMenu, type KebabMenuOption } from '@/components/ui/KebabMenu';
import type { EntityType } from '@/store/pendingChangesStore';

/**
 * Generic parent/children entity tree renderer for the Campaign
 * Structure card. The non-PMax Google branch (ad groups + nested ads)
 * and the Meta branch (ad sets + nested ads) are structurally identical
 * down to Tailwind classes and SVG paths — this component is the shared
 * markup. Google's PMax branch (asset groups, no children) also flows
 * through this by passing parents with `children: undefined`.
 *
 * Platform-specific concerns stay in the caller:
 *  - `<StatusBadge>` (Google uses `getStatusBadgeClasses` vocab;
 *    Meta uses `getMetaStatusBadgeClasses`)
 *  - `getMenuOptions` (terminal action label/variant — `Remove`/danger
 *    vs `Archive`/warning, plus the disabled state coming from the
 *    pending-change check in the hook)
 *  - The normalizer building `parents` (`ad_group_id`/`ad_set_id`,
 *    Google's RDA/DemandGen ad-name formatting vs Meta's simple
 *    `ad.ad_name` fallback)
 */

export interface NormalizedTreeChild {
  id: string;
  name: string;
  status: string;
  entityType: EntityType;
  /** Parent's id (`adGroupId` or `adSetId`) — needed when queueing a status change for the child. */
  parentId: string;
  /** Short prefix shown before the name, e.g. `RSA`, `RDA`, `DG Multi-Asset`, `Ad`. */
  typeLabel?: string;
}

export interface NormalizedTreeParent {
  id: string;
  name: string;
  status: string;
  entityType: EntityType;
  /** Omit or pass an empty array for single-tier parents (PMax asset groups). */
  children?: NormalizedTreeChild[];
}

export interface GetMenuOptionsParams {
  type: EntityType;
  entityId: string;
  entityName: string;
  status: string;
  /** Set only when `type === 'ad'` — the parent's id. */
  parentId?: string;
}

interface EntityTreeListProps {
  parents: NormalizedTreeParent[];
  StatusBadge: React.ComponentType<{ status: string; entityId: string }>;
  getMenuOptions: (params: GetMenuOptionsParams) => KebabMenuOption[];
}

const FolderIcon = () => (
  <svg
    className="w-4 h-4 text-gray-400 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    />
  </svg>
);

const DocumentIcon = () => (
  <svg
    className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

export function EntityTreeList({
  parents,
  StatusBadge,
  getMenuOptions,
}: EntityTreeListProps) {
  return (
    <div className="space-y-1">
      {parents.map((parent) => {
        const hasChildren = !!parent.children && parent.children.length > 0;
        return (
          <div key={parent.id}>
            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FolderIcon />
                <span className="text-sm text-gray-900 truncate">{parent.name}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <StatusBadge status={parent.status} entityId={parent.id} />
                <KebabMenu
                  options={getMenuOptions({
                    type: parent.entityType,
                    entityId: parent.id,
                    entityName: parent.name,
                    status: parent.status,
                  })}
                  ariaLabel={`Actions for ${parent.name}`}
                />
              </div>
            </div>

            {hasChildren && (
              <div className="ml-6 border-l-2 border-gray-200 pl-3 space-y-1 mt-1">
                {parent.children!.map((child) => (
                  <div
                    key={child.id}
                    className="flex items-center justify-between py-1.5 px-2 hover:bg-gray-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <DocumentIcon />
                      {child.typeLabel && (
                        <span className="text-xs font-medium text-blue-600 flex-shrink-0">
                          {child.typeLabel}:
                        </span>
                      )}
                      <span className="text-xs text-gray-700 truncate">{child.name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <StatusBadge status={child.status} entityId={child.id} />
                      <KebabMenu
                        options={getMenuOptions({
                          type: child.entityType,
                          entityId: child.id,
                          entityName: child.name,
                          status: child.status,
                          parentId: child.parentId,
                        })}
                        ariaLabel={`Actions for ${child.name}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default EntityTreeList;
